/**
 * Transaction Cancellation Success Screen
 *
 * Confirmation screen shown after successful transaction cancellation
 * Shows success message and allows user to continue
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { RampsButton } from "@/components/ramps/Button";
import { useWebViewIntegration } from "@/lib/url-state";
import { useRampsStore } from "@/stores/use-ramps-store";
import { RampsText } from "../components/ramps/Text";

export const Route = createFileRoute("/ramps/cancel-success")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			wallet_provider?: string;
			lang?: string;
		},
	component: CancellationSuccessPage,
});

function CancellationSuccessPage() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const { sendToWebView } = useWebViewIntegration();
	const rampsState = useRampsStore();

	const handleDone = () => {
		// Warn if sep24TransactionResponse is missing (v5 format)
		if (
			rampsState.commitResponse &&
			!rampsState.commitResponse.sep24TransactionResponse
		) {
			console.warn(
				"[Cancel Success] sep24TransactionResponse missing (v5 format), using fallback",
			);
		}

		// Prepare transaction response
		const transactionResponse = rampsState.commitResponse
			?.sep24TransactionResponse || {
			id: search.transaction_id || rampsState.transactionId,
			status: "cancelled",
			more_info_url: "",
			stellar_transaction_id: "",
			external_transaction_id:
				search.transaction_id || rampsState.transactionId,
		};

		// Send transaction in legacy format to parent window
		if (window.parent && window.parent !== window) {
			window.parent.postMessage(
				{
					transaction: transactionResponse,
				},
				"*",
			);
		}

		navigate({
			to: "/transaction-status",
			search: {
				transaction_id: search.transaction_id || "",
				token: search.token || "",
				lang: rampsState.lang || "en",
				// biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires flexible search param type
			} as any,
		});

		// Also use the existing WebView integration for additional compatibility
		sendToWebView("TRANSACTION_CANCELLED", {
			transactionId: search.transaction_id || rampsState.transactionId,
			status: "cancelled",
			referenceNumber: rampsState.commitResponse?.referenceNumber || "",
			token: search.token || rampsState.token,
			message: "Transaction cancelled successfully",
			timestamp: new Date().toISOString(),
		});
	};

	return (
		<div
			className="min-h-screen flex flex-col"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			{/* Content */}
			<div className="flex-1 flex flex-col items-center justify-items-start px-4 text-center pt-8">
				{/* Success Icon */}
				<div className="mb-8">
					<div className="w-16 h-16 rounded-full flex items-center justify-center relative bg-gradient-ramps-status-bl">
						<Check
							size={32}
							style={{ color: "var(--ramps-text)" }}
							strokeWidth={1}
						/>
					</div>
				</div>

				{/* Success Message */}
				<div className="mb-8">
					<RampsText variant="title-lg-bold" color="primary" className="mb-4">
						Cancellation successful
					</RampsText>
					<RampsText
						variant="body-default"
						color="secondary"
						className="max-w-sm"
					>
						Your refund will be credited to your wallet shortly
					</RampsText>
				</div>
			</div>

			{/* Bottom Button */}
			<div className="p-4">
				<RampsButton
					variant="primary"
					size="large"
					className="w-full"
					onClick={handleDone}
				>
					Done
				</RampsButton>
			</div>
		</div>
	);
}
